// /////////////////////////////////////////////////////////////
// [ファイル] usePdfTemplate.ts
// [概要] 帳票テンプレートフォーマット (固定レイアウトに名前を付けたもの) の
//        一覧・登録・削除・プレビューを担うフック。共有範囲の判定はバックエンドが権威的に
//        行うため Firestore 直アクセスせず Node 関数経由で扱う。
//        記述内容(自社情報・宛先・明細)の DB 参照連携は後フェーズ。
// /////////////////////////////////////////////////////////////
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../firebase/AuthProvider';
import { getIdToken } from '../firebase/getIdToken';

const BASE = 'https://asia-northeast1-plexiform-bot-455507-h3.cloudfunctions.net';
const LIST_URL = `${BASE}/getPdfTemplate`;
const SAVE_URL = `${BASE}/savePdfTemplate`;
const DELETE_URL = `${BASE}/deletePdfTemplate`;
const PREVIEW_URL = `${BASE}/previewPdfTemplate`;

export type TemplateScope = 'personal' | 'shared';
export type BaseLayout = 'quote' | 'generic';

export interface TemplateFormat {
  id: string;
  name: string;
  base_layout: BaseLayout;
  scope: TemplateScope;
  user_id: string;
  shared_with: string[]; // scope='shared' の共有メンバー email (作成者を含む)
  enabled: boolean;
  // generic フォーマットのレイアウト定義 (枠)
  spec?: Record<string, unknown>;
}

export interface SaveFormatInput {
  id?: string;
  name: string;
  base_layout?: BaseLayout;
  scope: TemplateScope;
}

export const usePdfTemplate = () => {
  const { user } = useAuth();
  const [formats, setFormats] = useState<TemplateFormat[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchFormats = useCallback(async () => {
    if (!user?.email) return;
    setLoading(true);
    try {
      const token = await getIdToken();
      if (!token) return;
      const res = await axios.post(
        LIST_URL,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status === 'success') {
        setFormats(res.data.formats ?? []);
      }
    } catch (err) {
      console.error('帳票フォーマットの取得に失敗しました', err);
    } finally {
      setLoading(false);
    }
  }, [user?.email]);

  useEffect(() => {
    void fetchFormats();
  }, [fetchFormats]);

  // フォーマットを登録・更新する
  const save = useCallback(
    async (input: SaveFormatInput): Promise<boolean> => {
      const token = await getIdToken();
      if (!token) return false;
      const res = await axios.post(SAVE_URL, input, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const ok = res.data.status === 'success';
      if (ok) await fetchFormats();
      else throw new Error(res.data.message || '保存に失敗しました');
      return ok;
    },
    [fetchFormats]
  );

  // フォーマットを削除する
  const remove = useCallback(
    async (id: string): Promise<boolean> => {
      const token = await getIdToken();
      if (!token) return false;
      const res = await axios.post(
        DELETE_URL,
        { id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const ok = res.data.status === 'success';
      if (ok) await fetchFormats();
      else throw new Error(res.data.message || '削除に失敗しました');
      return ok;
    },
    [fetchFormats]
  );

  // フォーマットのプレビュー PDF を生成し、表示用の Blob URL を返す。
  // プレビューは Drive に保存せず、バックエンドが base64 で返した PDF をその場で
  // Blob 化してモーダル表示する (確認用のため Drive を汚さない)。
  // generic は id 指定で保存済みの枠(spec)を描画、quote 等はサンプル内容で描画する。
  const preview = useCallback(
    async (
      opts: { id?: string; base_layout?: BaseLayout } = {}
    ): Promise<{ blobUrl: string; fileName: string } | null> => {
      const token = await getIdToken();
      if (!token) return null;
      const res = await axios.post(
        PREVIEW_URL,
        { id: opts.id, base_layout: opts.base_layout ?? 'quote' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.status !== 'success') {
        throw new Error(res.data.message || 'プレビュー生成に失敗しました');
      }
      const b64 = res.data.pdf_base64 as string | undefined;
      if (!b64) {
        // 後方互換: 旧仕様 (Drive リンク) のレスポンスは新しいタブで開けるよう URL を返す
        const link = res.data.web_view_link as string | undefined;
        if (link) return { blobUrl: link, fileName: res.data.file_name ?? 'preview.pdf' };
        throw new Error('プレビュー PDF を取得できませんでした');
      }
      // base64 → Blob → オブジェクト URL (iframe で表示する)
      const bytes = Uint8Array.from(atob(b64), (c) => c.charCodeAt(0));
      const blob = new Blob([bytes], { type: res.data.pdf_mime ?? 'application/pdf' });
      return { blobUrl: URL.createObjectURL(blob), fileName: res.data.file_name ?? 'preview.pdf' };
    },
    []
  );

  return { formats, loading, save, remove, preview, refetch: fetchFormats };
};
